import type { RangePickerValueType } from '@sede-enterprise/shell-ds-react-framework';
import {
  Icons,
  RangeDatePicker,
  Table,
  Tabs,
  Tooltip,
} from '@sede-enterprise/shell-ds-react-framework';
import {
  TBody,
  TD as Td,
  TH as Th,
  THead,
  TR as Tr,
} from '@sede-enterprise/shell-ds-react-framework/build/esm/components/Table/components/TableElements/TableElements';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { COMMON_MESSAGES } from 'src/constants/Common';
import { GET_EXTERNAL_INBOX, GET_EXTERNAL_INBOX_BLOCK } from 'src/constants/EUMarkets';
import { get, post } from 'src/services/GenesisApi';
import Button from 'src/UI/Button';
import Loader from 'src/UI/Loader';
import { getCurrentGasDay, handleError } from 'src/utils/common/utils';
import { Modal } from '../../SystemAdmin/NominationGroups/style';
import { EXTERNAL_INBOX_BATCH_SIZE } from './constants';
import { ExternalInboxContainer, ExternalInboxModalGlobalStyle } from './style';
import { IExternalInbox, IExternalInboxBlock, IExternalInboxRequest } from './types';

const {
  AlertCircleSolid,
  ArrowCw,
  ArrowDownSquare,
  ArrowUpSquare,
  CheckCircleSolid,
  ChevronUpDown,
  CrossCircleSolid,
  EyeSolid,
  MinusCircleSolid,
} = Icons;

const DATE_YYYY_MM_DD = 'YYYY-MM-DD';
const INLINE_FLEX = 'inline-flex';
const ICON_SIZE = 18;
const COLOR_SUCCESS = '#22a06b';
const COLOR_ERROR = '#e3342f';
const COLOR_PENDING = '#f9a825';
const COLOR_NEUTRAL = '#9ca3af';

type SortDir = 'asc' | 'desc' | null;
type SortableColumn =
  | 'receivedTimeUtc'
  | 'source'
  | 'messageType'
  | 'sender'
  | 'receiver'
  | 'periodFrom'
  | 'periodTo';
type StatusColumn = 'received' | 'processed' | 'error';

const STATUS_COLUMNS: { key: StatusColumn; label: string }[] = [
  { key: 'received', label: 'Received' },
  { key: 'processed', label: 'Processed' },
  { key: 'error', label: 'Error' },
];

const COLUMNS: { key: SortableColumn; label: string; className: string }[] = [
  { key: 'messageType', label: 'Message Type', className: 'col-message-type' },
  { key: 'periodFrom', label: 'Period From', className: 'col-period' },
  { key: 'periodTo', label: 'Period To', className: 'col-period' },
  { key: 'sender', label: 'Sender', className: 'col-party' },
  { key: 'receiver', label: 'Receiver', className: 'col-party' },
  { key: 'source', label: 'Source', className: 'col-source' },
  { key: 'receivedTimeUtc', label: 'Received Time (UTC)', className: 'col-datetime' },
];

export const formatDateTime = (iso?: string | null): string => {
  if (!iso) {
    return '';
  }
  const d = dayjs(iso);
  return d.isValid() ? d.format('DD MMM YYYY HH:mm') : iso;
};

const DATE_COLUMNS = new Set<SortableColumn>(['receivedTimeUtc', 'periodFrom', 'periodTo']);

const getCellValue = (row: IExternalInbox, key: SortableColumn): string =>
  DATE_COLUMNS.has(key) ? formatDateTime(row[key]) : (row[key] ?? '');

const XML_INDENT = '  ';

// API returns single-line XML; indent it one element per line for readability
export const formatXml = (xml?: string | null): string => {
  if (!xml) {
    return '';
  }
  let depth = 0;
  return xml
    .trim()
    .replace(/>\s*</g, '>\n<')
    .split('\n')
    .map((line) => {
      const isClosing = line.startsWith('</');
      const isOpeningCandidate = line.startsWith('<') && !isClosing && !line.endsWith('/>');
      const isDeclaration = line.startsWith('<?') || line.startsWith('<!');
      const isOpening = isOpeningCandidate && !isDeclaration && !line.includes('</');
      if (isClosing) {
        depth = Math.max(depth - 1, 0);
      }
      const indented = XML_INDENT.repeat(depth) + line;
      if (isOpening) {
        depth += 1;
      }
      return indented;
    })
    .join('\n');
};

interface SortIconProps {
  col: SortableColumn;
  sortColumn: SortableColumn | null;
  sortDir: SortDir;
}

const SortIcon = ({ col, sortColumn, sortDir }: SortIconProps) => {
  if (sortColumn !== col || !sortDir) {
    return <ChevronUpDown className='sort-icon' />;
  }
  return sortDir === 'asc' ? (
    <ArrowUpSquare className='sort-icon' />
  ) : (
    <ArrowDownSquare className='sort-icon' />
  );
};

type StatusState = 'yes' | 'no' | 'pending';

const statusIcon = (state: StatusState, isFailure = false) => {
  if (state === 'pending') {
    return (
      <AlertCircleSolid width={ICON_SIZE} height={ICON_SIZE} style={{ color: COLOR_PENDING }} />
    );
  }
  if (state === 'no') {
    return (
      <MinusCircleSolid width={ICON_SIZE} height={ICON_SIZE} style={{ color: COLOR_NEUTRAL }} />
    );
  }
  return isFailure ? (
    <CrossCircleSolid width={ICON_SIZE} height={ICON_SIZE} style={{ color: COLOR_ERROR }} />
  ) : (
    <CheckCircleSolid width={ICON_SIZE} height={ICON_SIZE} style={{ color: COLOR_SUCCESS }} />
  );
};

export const getStatus = (
  column: StatusColumn,
  row: IExternalInbox,
): { state: StatusState; label: string } => {
  if (column === 'received') {
    return row.isReceived
      ? { state: 'yes', label: 'Received' }
      : { state: 'no', label: 'Not received' };
  }
  if (column === 'processed') {
    if (row.isProcessed) {
      return { state: 'yes', label: 'Processed' };
    }
    return row.isError
      ? { state: 'no', label: 'Not processed' }
      : { state: 'pending', label: 'Pending processing' };
  }
  return row.isError
    ? { state: 'yes', label: row.errorMessage?.trim() || 'Processing failed' }
    : { state: 'no', label: 'No error' };
};

const StatusCell = ({ column, row }: { column: StatusColumn; row: IExternalInbox }) => {
  const { state, label } = getStatus(column, row);
  return (
    <Tooltip
      triggerAction='hover'
      overlay={<div className='external-inbox-status-tooltip'>{label}</div>}
      trigger={
        <span
          className='status-icon'
          style={{ display: INLINE_FLEX }}
          data-testid={`external-inbox-${column}-${state}-${row.id}`}
        >
          {statusIcon(state, column === 'error')}
        </span>
      }
    />
  );
};

const fetchExternalInbox = (req: IExternalInboxRequest): Promise<IExternalInbox[]> =>
  post(GET_EXTERNAL_INBOX, req as unknown as Record<string, unknown>).then((r) => r.data ?? []);

const fetchExternalInboxBlocks = (inboxId: number): Promise<IExternalInboxBlock[]> =>
  get(`${GET_EXTERNAL_INBOX_BLOCK}?inboxId=${inboxId}`).then((r) => r.data ?? []);

const ExternalInbox = () => {
  const [visibleRows, setVisibleRows] = useState<IExternalInbox[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [refreshCount, setRefreshCount] = useState(0);

  const [periodFrom, setPeriodFrom] = useState<string>(() =>
    getCurrentGasDay().format(DATE_YYYY_MM_DD),
  );
  const [periodTo, setPeriodTo] = useState<string>(() =>
    getCurrentGasDay().format(DATE_YYYY_MM_DD),
  );

  const [sortColumn, setSortColumn] = useState<SortableColumn | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>(null);

  // View popup
  const [selectedRow, setSelectedRow] = useState<IExternalInbox | null>(null);
  const [blockData, setBlockData] = useState<IExternalInboxBlock[]>([]);
  const [activeBlockKey, setActiveBlockKey] = useState<string>('');
  const [isLoadingBlocks, setIsLoadingBlocks] = useState(false);

  const hasMoreRef = useRef(true);
  const nextPageRef = useRef(1);
  const isFetchingRef = useRef(false);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const filterKey = `${periodFrom}|${periodTo}|${refreshCount}`;

  // API has no sort params, so sorting applies to the rows loaded so far
  const displayRows = useMemo(() => {
    if (!sortColumn || !sortDir) {
      return visibleRows;
    }
    const direction = sortDir === 'asc' ? 1 : -1;
    return [...visibleRows].sort(
      (a, b) => direction * String(a[sortColumn] ?? '').localeCompare(String(b[sortColumn] ?? '')),
    );
  }, [visibleRows, sortColumn, sortDir]);

  const loadMoreRows = useCallback(() => {
    if (isFetchingRef.current || !hasMoreRef.current) {
      return;
    }
    isFetchingRef.current = true;
    setIsLoading(true);
    setHasError(false);

    const req: IExternalInboxRequest = {
      fromDate: periodFrom,
      toDate: periodTo,
      pageNumber: nextPageRef.current,
      pageSize: EXTERNAL_INBOX_BATCH_SIZE,
    };

    fetchExternalInbox(req)
      .then((rows) => {
        setVisibleRows((prev) => [...prev, ...rows]);
        if (rows.length > 0) {
          setTotalCount(rows[0].totalCount);
        }
        nextPageRef.current += 1;
        const loadedSoFar = (nextPageRef.current - 1) * EXTERNAL_INBOX_BATCH_SIZE;
        const knownTotal = rows[0]?.totalCount ?? 0;
        hasMoreRef.current = rows.length === EXTERNAL_INBOX_BATCH_SIZE && loadedSoFar < knownTotal;
        setIsLoading(false);
        isFetchingRef.current = false;
      })
      .catch((err) => {
        handleError(err, COMMON_MESSAGES.ERROR);
        setHasError(true);
        setIsLoading(false);
        isFetchingRef.current = false;
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey]);

  // Reset on filter change — must run before the [loadMoreRows] effect
  useEffect(() => {
    nextPageRef.current = 1;
    isFetchingRef.current = false;
    hasMoreRef.current = true;
    setVisibleRows([]);
    setTotalCount(null);
    setHasError(false);
    setIsLoading(false);
  }, [filterKey]);

  useEffect(() => {
    loadMoreRows();
  }, [loadMoreRows]);

  // IntersectionObserver for infinite scroll
  useEffect(() => {
    const sentinelNode = sentinelRef.current;
    if (!sentinelNode) {
      return () => {};
    }
    let scrollParent: Element | null = null;
    let node: HTMLElement | null = sentinelNode.parentElement;
    while (node) {
      const { overflow, overflowY } = globalThis.getComputedStyle(node);
      if (/auto|scroll/.test(overflow + overflowY)) {
        scrollParent = node;
        break;
      }
      node = node.parentElement;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          loadMoreRows();
        }
      },
      { root: scrollParent, rootMargin: '300px', threshold: 0 },
    );
    observer.observe(sentinelNode);
    return () => observer.disconnect();
  }, [loadMoreRows]);

  const handleSortCol = (col: SortableColumn) => {
    if (sortColumn !== col) {
      setSortColumn(col);
      setSortDir('asc');
      return;
    }
    if (sortDir === 'asc') {
      setSortDir('desc');
      return;
    }
    setSortDir(null);
    setSortColumn(null);
  };

  const handleViewClick = (row: IExternalInbox) => {
    setSelectedRow(row);
    setBlockData([]);
    setActiveBlockKey('');
    setIsLoadingBlocks(true);
    fetchExternalInboxBlocks(row.id)
      .then((data) => {
        setBlockData(data);
        setIsLoadingBlocks(false);
      })
      .catch((err) => {
        handleError(err, 'Failed to load in-house message details.');
        setIsLoadingBlocks(false);
      });
  };

  const handleCloseModal = () => {
    setSelectedRow(null);
    setBlockData([]);
  };

  const renderBlocks = () => {
    if (isLoadingBlocks) {
      return (
        <div className='external-inbox-blocks-loading' data-testid='external-inbox-blocks-loading'>
          <Loader />
        </div>
      );
    }
    if (blockData.length === 0) {
      return <div className='external-inbox-blocks-empty'>No message details found</div>;
    }
    const blockTabs = blockData.map((block, index) => ({
      key: String(block.id),
      label: `Block ${index + 1}`,
      children: (
        <div className='external-inbox-block' data-testid={`external-inbox-block-${block.id}`}>
          {block.createdTimeInUtc && (
            <div className='external-inbox-block-time'>
              Created (UTC): {formatDateTime(block.createdTimeInUtc)}
            </div>
          )}
          <pre className='external-inbox-block-content'>{formatXml(block.inHouseXml)}</pre>
        </div>
      ),
    }));
    return (
      <>
        <div className='external-inbox-block-count' data-testid='external-inbox-block-count'>
          {blockData.length} {blockData.length === 1 ? 'block' : 'blocks'}
        </div>
        <Tabs
          className='external-inbox-block-tabs'
          activeKey={activeBlockKey || blockTabs[0].key}
          onChange={setActiveBlockKey}
          items={blockTabs}
        />
      </>
    );
  };

  return (
    <ExternalInboxContainer>
      <ExternalInboxModalGlobalStyle />
      <div className='filters-card'>
        <div className='filters-row'>
          <div className='filter-field'>
            <span className='filter-label'>Received date range</span>
            <RangeDatePicker
              format='DD MMM YYYY'
              allowClear={false}
              value={[dayjs(periodFrom), dayjs(periodTo)]}
              onChange={(val: RangePickerValueType) => {
                if (!val) {
                  return;
                }
                const [from, to] = val;
                if (from) {
                  setPeriodFrom(dayjs(from).format(DATE_YYYY_MM_DD));
                }
                if (to) {
                  setPeriodTo(dayjs(to).format(DATE_YYYY_MM_DD));
                }
              }}
              size='small'
              data-testid='external-inbox-date-range'
            />
          </div>
          <div className='filters-refresh'>
            <Button
              size='small'
              iconOnly
              icon={<ArrowCw />}
              onClick={() => setRefreshCount((c) => c + 1)}
              disabled={isLoading}
              data-testid='external-inbox-refresh'
              title='Refresh'
            />
          </div>
        </div>
      </div>

      <div className='table-wrapper'>
        <div
          className={`table-container${isLoading ? ' table-loading' : ''}`}
          data-testid='external-inbox-scroll-area'
        >
          <Table className='shell-table' size='small'>
            <THead>
              <Tr className='table-row'>
                {STATUS_COLUMNS.map(({ key, label }) => (
                  <Th key={key} className='table-column col-status'>
                    {label}
                  </Th>
                ))}
                {COLUMNS.map(({ key, label, className }) => (
                  <Th key={key} className={`table-column ${className}`}>
                    <button
                      className='th-sort-trigger'
                      onClick={() => handleSortCol(key)}
                      data-testid={`external-inbox-sort-${key}`}
                    >
                      {label}
                      <SortIcon col={key} sortColumn={sortColumn} sortDir={sortDir} />
                    </button>
                  </Th>
                ))}
                <Th className='table-column col-view'>View</Th>
              </Tr>
            </THead>
            <TBody>
              {displayRows.map((row) => (
                <Tr key={row.id} className='table-row'>
                  {STATUS_COLUMNS.map(({ key }) => (
                    <Td key={key} className='table-column col-status'>
                      <StatusCell column={key} row={row} />
                    </Td>
                  ))}
                  {COLUMNS.map(({ key, className }) => {
                    const value = getCellValue(row, key);
                    return (
                      <Td key={key} className={`table-column ${className}`} title={value}>
                        {value}
                      </Td>
                    );
                  })}
                  <Td className='table-column col-view'>
                    <Button
                      variant='transparent'
                      title='View In-house Message'
                      size='small'
                      onClick={() => handleViewClick(row)}
                      data-testid={`external-inbox-view-${row.id}`}
                      icon={<EyeSolid />}
                      iconOnly
                    />
                  </Td>
                </Tr>
              ))}
              {visibleRows.length === 0 && !isLoading && (
                <Tr className='table-row'>
                  <Td
                    colSpan={STATUS_COLUMNS.length + COLUMNS.length + 1}
                    className='table-column empty-cell'
                  >
                    {hasError
                      ? 'Failed to load data. Please try again.'
                      : 'No records match the selected filters'}
                  </Td>
                </Tr>
              )}
            </TBody>
          </Table>
          <div ref={sentinelRef} className='load-sentinel' />
        </div>

        {isLoading && (
          <div className='loading-more' data-testid='external-inbox-loading'>
            <Loader />
          </div>
        )}
      </div>

      {visibleRows.length > 0 && (
        <div className='row-count' data-testid='external-inbox-row-count'>
          <span className='row-count-loaded'>
            {visibleRows.length.toLocaleString('en-GB')} loaded
          </span>
          {totalCount !== null && (
            <>
              <span className='row-count-sep'>·</span>
              <span className='row-count-total'>
                {totalCount.toLocaleString('en-GB')} Total Records
              </span>
            </>
          )}
        </div>
      )}

      {selectedRow && (
        <Modal
          className='external-inbox-modal'
          title='In-house Message'
          open
          onClose={handleCloseModal}
          maskClosable={false}
          isDraggable
          width={1100}
          footer={
            <div className='button-style'>
              <Button variant='outlined' onClick={handleCloseModal}>
                Close
              </Button>
            </div>
          }
        >
          <div className='external-inbox-modal-body'>
            <div className='external-inbox-reference'>
              <strong>Sender:</strong> {selectedRow.sender} &nbsp;|&nbsp;{' '}
              <strong>Received (UTC):</strong> {formatDateTime(selectedRow.receivedTimeUtc)}
            </div>
            {renderBlocks()}
          </div>
        </Modal>
      )}
    </ExternalInboxContainer>
  );
};

export default ExternalInbox;
